import { apiFetch } from "./apiFetch";

export type PartnerStatus = "Active" | "Inactive";

export type PartnerLinkPlatform =
  | "Website"
  | "Instagram"
  | "Facebook"
  | "Twitter"
  | "LinkedIn"
  | "YouTube"
  | "TikTok"
  | "Discourse"
  | "Other";

export interface PartnerLink {
  platform: PartnerLinkPlatform;
  url: string;
  label?: string;
  iconUrl?: string;
}

export interface Partner {
  id: string;
  groupId: string;
  displayName: string;
  hashtag: string;
  aboutText?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  links?: PartnerLink[];
  discourseGroupName?: string;
  discourseCategoryUrl?: string;
  status: PartnerStatus;
  // Per-partner transactional-email toggles (ENG-895). Nullable: absent ⇒ send.
  sendWelcomeEmail?: boolean;
  sendOffboardingEmail?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePartnerRequest {
  displayName: string;
  hashtag: string;
  aboutText?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  links?: PartnerLink[];
  discourseGroupName?: string;
  discourseCategoryUrl?: string;
  sendWelcomeEmail?: boolean;
  sendOffboardingEmail?: boolean;
}

export type UpdatePartnerRequest = Partial<CreatePartnerRequest> & {
  status?: PartnerStatus;
};

interface PartnersListResponse {
  partners: Partner[];
}

export interface AssetUploadUrlRequest {
  fileName: string;
  contentType: string;
}

export interface AssetUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  s3Key: string;
}

export interface PartnerMember {
  appUserId: string;
  email: string;
  displayName: string;
  revenueCatId: string;
}

export type ProvisionStatus = "created" | "attached" | "already_member";

export interface ProvisionResult {
  email: string;
  status: ProvisionStatus;
  appUserId: string;
  revenueCatId: string;
  alreadyHadActiveSubscription: boolean;
}

export interface OffboardResult {
  email: string;
  appUserId: string;
  revenueCatId: string;
  rcRevoked: boolean;
  userGroupDeleted: boolean;
  tagDeleted: boolean;
}

export interface BulkRowResult {
  email: string;
  displayName: string;
  status: ProvisionStatus | "error";
  appUserId?: string;
  alreadyHadActiveSubscription?: boolean;
  error?: string;
}

export interface BulkImportSummary {
  total: number;
  created: number;
  attached: number;
  alreadyMember: number;
  error: number;
  alreadyHadActiveSubscription: number;
}

export interface BulkImportResponse {
  summary: BulkImportSummary;
  results: BulkRowResult[];
}

interface PartnerMembersResponse {
  members: PartnerMember[];
}

export const PartnerResource = {
  getList: () =>
    apiFetch<PartnersListResponse>("/admin/partners").then((r) => r.partners),

  listMembers: (partnerId: string) =>
    apiFetch<PartnerMembersResponse>(
      `/admin/partner/${partnerId}/members`
    ).then((r) => r.members),

  addMember: (
    partnerId: string,
    data: { email: string; displayName: string; password?: string }
  ) =>
    apiFetch<ProvisionResult>(`/admin/partner/${partnerId}/member`, {
      method: "POST",
      body: data,
    }),

  removeMember: (
    partnerId: string,
    ref: { appUserId?: string; email?: string }
  ) =>
    apiFetch<OffboardResult>(`/admin/partner/${partnerId}/member`, {
      method: "DELETE",
      body: ref,
    }),

  bulkImport: (partnerId: string, csv: string) =>
    apiFetch<BulkImportResponse>(`/admin/partner/${partnerId}/members/bulk`, {
      method: "POST",
      body: { csv },
    }),

  getById: (partnerId: string) =>
    apiFetch<Partner>(`/admin/partner/${partnerId}`),

  create: (data: CreatePartnerRequest) =>
    apiFetch<Partner>("/admin/partner", { method: "POST", body: data }),

  update: (partnerId: string, data: UpdatePartnerRequest) =>
    apiFetch<Partner>(`/admin/partner/${partnerId}`, {
      method: "PATCH",
      body: data,
    }),

  getAssetUploadUrl: (partnerId: string, data: AssetUploadUrlRequest) =>
    apiFetch<AssetUploadUrlResponse>(
      `/admin/partner/${partnerId}/asset-upload-url`,
      { method: "POST", body: data }
    ),
};

/**
 * Upload a logo/asset for an existing partner: request a presigned URL, PUT the
 * file straight to S3, and return the public URL to persist as logoUrl.
 */
export const uploadPartnerAsset = async (
  partnerId: string,
  file: File
): Promise<string> => {
  const { uploadUrl, publicUrl } = await PartnerResource.getAssetUploadUrl(
    partnerId,
    { fileName: file.name, contentType: file.type }
  );

  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Asset upload failed: ${response.status}`);
  }

  return publicUrl;
};
